/* 차트 패턴 도식 + 방향 분류 — TickerPage 차트분석 탭과 차트 패턴 사전(/chart-patterns)에서 공용 사용.
 *  도식은 public/images/patterns/{file}.svg 파일을 <img> 로 참조(원본 색상 그대로). */
import { cn } from '@/lib/cn';

/* ── 방향(강세/약세/중립) ─────────────────────────────────── */
export type PatternDir = '강세' | '약세' | '중립';

/** 패턴 SVG 이미지 — public/images/patterns/{file}.svg.
 *  file 이 비어 있으면(미분류) 간단한 중립 라인 도식을 인라인으로 표시. */
export function PatternImg({
  file,
  name,
  className,
}: {
  file: string;
  name?: string;
  className?: string;
}) {
  if (!file) {
    // 미분류 fallback — 다크 카드 톤에 맞춘 단순 추이 라인
    return (
      <svg
        viewBox="0 0 100 65"
        className={className}
        role="img"
        aria-label={name ?? '패턴 도식'}
        style={{ background: '#111', borderRadius: 6 }}
      >
        <polyline
          points="6,42 24,30 40,38 58,18 76,32 94,12"
          fill="none"
          stroke="#888"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <img
      src={`${import.meta.env.BASE_URL}images/patterns/${file}.svg`}
      alt={name ?? file}
      loading="lazy"
      className={className}
    />
  );
}

/** AI 패턴명 → SVG 파일 + 방향 매핑 규칙.
 *  영문 패턴명은 파일명 규칙(영숫자 외 → '_')과 직접 매칭, 한글은 키워드로 유사 패턴 매핑. */
type Rule = { kws: string[]; file: string; dir: PatternDir };
const RULES: Rule[] = [
  // 2026-05-28 chart_patterns_87_rebuilt.html 기준 재구성. 87개 lowercase snake_case 파일과 1:1.
  // 더 구체적인 키워드를 먼저 배치(역헤드앤숄더 → 헤드앤숄더, 약세*하모닉 → 일반 하모닉).
  // ── 반전-하락 ──
  { kws: ['역헤드앤숄더', '역헤드', '역머리어깨', '역삼산', 'invertedhs', 'inversehead'], file: 'inv_head_shoulders', dir: '강세' },
  { kws: ['헤드앤숄더', '머리어깨', '삼산', 'headshoulders', 'head&shoulders'], file: 'head_shoulders', dir: '약세' },
  { kws: ['삼중바닥', '삼중바텀', '트리플바텀', 'triplebottom'], file: 'triple_bottom', dir: '강세' },
  { kws: ['삼중탑', '삼중천장', '트리플탑', 'tripletop'], file: 'triple_top', dir: '약세' },
  { kws: ['더블바닥', '쌍바닥', '이중바닥', '더블바텀', 'doublebottom'], file: 'double_bottom', dir: '강세' },
  { kws: ['더블탑', '쌍봉', '쌍고점', '이중천장', '이중천정', 'doubletop'], file: 'double_top', dir: '약세' },
  { kws: ['다이아몬드바닥', '다이아몬드바텀', 'diamondbottom'], file: 'diamond_bottom', dir: '강세' },
  { kws: ['다이아몬드탑', '다이아몬드천장', '다이아몬드', 'diamondtop'], file: 'diamond_top', dir: '약세' },
  { kws: ['라운딩바텀', '원형바닥', '소서바닥', '둥근바닥', 'roundingbottom'], file: 'rounding_bottom', dir: '강세' },
  { kws: ['라운딩탑', '원형천장', '소서탑', 'roundingtop'], file: 'rounding_top', dir: '약세' },
  { kws: ['v자바닥', 'v바닥', 'v자', 'vbottom'], file: 'v_bottom', dir: '강세' },
  { kws: ['아일랜드반전바텀', '아일랜드바닥', '강세아일랜드', '아일랜드반전강세'], file: 'island_bottom', dir: '강세' },
  { kws: ['아일랜드반전탑', '아일랜드', '섬반전', 'islandreversal'], file: 'island_top', dir: '약세' },
  { kws: ['브로드닝', '확장형', '메가폰', 'broadening'], file: 'broadening_top', dir: '약세' },
  { kws: ['블래스트오프', '블래스트', 'blastoff'], file: 'blastoff', dir: '약세' },
  { kws: ['베어리시브레이크다운', '베어브레이크다운', 'bearishbreakdown'], file: 'bearish_breakdown', dir: '약세' },
  // ── 반전-상승 ──
  { kws: ['자그바텀', '자그', 'jague'], file: 'jague_bottom', dir: '강세' },
  { kws: ['이스터링', 'eastering'], file: 'eastering', dir: '강세' },
  { kws: ['웁스', 'oops'], file: 'oops', dir: '강세' },
  { kws: ['와이코프스프링', '스프링와이코프', '스프링', 'wyckoffspring', 'spring'], file: 'spring_wyckoff', dir: '강세' },
  // ── 지속-상승 ──
  { kws: ['트리플샷', '세번매집', '트리플샷매집', 'tripleshot'], file: 'triple_shot', dir: '강세' },
  { kws: ['스캘럽', '스캘롭', '스캘롭앤핸들', 'scallop'], file: 'scallop_handle', dir: '강세' },
  { kws: ['컵앤핸들', '컵핸들', '컵위드핸들', 'cuphandle', 'cup&handle'], file: 'cup_handle', dir: '강세' },
  { kws: ['상승삼각', '어센딩삼각', 'ascendingtriangle'], file: 'ascending_triangle', dir: '강세' },
  { kws: ['상승채널', '상승추세대', 'ascendingchannel'], file: 'ascending_channel', dir: '강세' },
  { kws: ['상승플래그군', '상승깃발군', '플래그군', 'ascendingflags', 'ascflags'], file: 'asc_flags', dir: '강세' },
  { kws: ['로스후크', '로스훅', 'rosshook'], file: 'ross_hook', dir: '강세' },
  { kws: ['st와이코프', '와이코프st', '2차테스트', 'secondarytest'], file: 'st_wyckoff', dir: '강세' },
  { kws: ['마켓어큐뮬레이션', '매집', '축적', '어큐뮬레이션', 'accumulation', 'marketacc'], file: 'market_acc', dir: '강세' },
  { kws: ['상승돌파', '불돌파', '박스돌파', '브레이크아웃', 'breakout', 'bullbreakout'], file: 'breakout_bull', dir: '강세' },
  { kws: ['내부봉추세', '인사이드바추세', 'includingtrend', 'insidebar'], file: 'including_trend', dir: '강세' },
  { kws: ['불플래그', '상승깃발', '상승플래그', 'bullflag'], file: 'bull_flag', dir: '강세' },
  { kws: ['불페넌트', '상승페넌트', 'bullpennant'], file: 'bull_pennant', dir: '강세' },
  { kws: ['박스권', '직사각', '레인지', 'rectangle', 'box'], file: 'rectangle', dir: '중립' },
  // ── 지속-하락 ──
  { kws: ['베어플래그', '하락깃발', '하락플래그', 'bearflag'], file: 'bear_flag', dir: '약세' },
  { kws: ['베어페넌트', '하락페넌트', 'bearpennant'], file: 'bear_pennant', dir: '약세' },
  { kws: ['하락삼각', '디센딩삼각', 'descendingtriangle'], file: 'descending_triangle', dir: '약세' },
  { kws: ['하락채널', '하락추세대', 'descendingchannel'], file: 'descending_channel', dir: '약세' },
  { kws: ['하락플래그군', '하락깃발군', 'descendingflags', 'descflags'], file: 'desc_flags', dir: '약세' },
  { kws: ['계단형하락', '계단식하락', '스캘다운', 'scaledown'], file: 'scaledown', dir: '약세' },
  { kws: ['헤프트니스', '헤프티니스', '정체', 'heftiness'], file: 'heftiness', dir: '약세' },
  { kws: ['하락붕괴', '브레이크다운', '하단이탈', '지지이탈', '붕괴', 'breakdown'], file: 'breakdown_bear', dir: '약세' },
  // ── 지속-중립 ──
  { kws: ['대칭삼각', '삼각수렴', '수렴삼각', '삼각', 'symmetricaltriangle'], file: 'symmetrical_triangle', dir: '중립' },
  { kws: ['변동성수축', '볼라틸리티스퀴즈', '스퀴즈', '변동성압축', 'volatilitysqueeze', 'squeeze'], file: 'vol_squeeze', dir: '중립' },
  { kws: ['극도횡보', '플랫', '횡보', 'flatness'], file: 'flatness', dir: '중립' },
  { kws: ['익스팬션', '확장', '변동성확대', 'expansion'], file: 'expansion', dir: '중립' },
  // ── 갭 ──
  { kws: ['갭업', '갭상승', '상승갭', 'gapup'], file: 'gap_up', dir: '강세' },
  { kws: ['갭다운', '갭하락', '하락갭', 'gapdown'], file: 'gap_down', dir: '약세' },
  { kws: ['브레이크어웨이갭', '돌파갭', 'breakawaygap'], file: 'breakaway_gap', dir: '강세' },
  { kws: ['러너웨이갭', '진행갭', '계속갭', 'runawaygap'], file: 'runaway_gap', dir: '중립' },
  { kws: ['소진갭', '소멸갭', 'exhaustiongap'], file: 'exhaustion_gap', dir: '약세' },
  // ── 캔들 ──
  { kws: ['세병사', '적삼병', '세양봉', '삼백병', 'threewhitesoldiers'], file: 'three_soldiers', dir: '강세' },
  { kws: ['세까마귀', '흑삼병', '세음봉', 'threeblackcrows'], file: 'three_crows', dir: '약세' },
  { kws: ['역해머', '역망치', '인버티드해머', 'invertedhammer'], file: 'inv_hammer', dir: '강세' },
  { kws: ['해머', '망치', 'hammer'], file: 'hammer', dir: '강세' },
  { kws: ['행잉맨', '걸린남자', '교수형', 'hangingman'], file: 'hanging_man', dir: '약세' },
  { kws: ['슈팅스타', '유성', '별똥', 'shootingstar'], file: 'shooting_star', dir: '약세' },
  { kws: ['도지', '십자', 'doji'], file: 'doji', dir: '중립' },
  { kws: ['모닝스타', '샛별', 'morningstar'], file: 'morning_star', dir: '강세' },
  { kws: ['이브닝스타', '석별', '저녁별', 'eveningstar'], file: 'evening_star', dir: '약세' },
  { kws: ['베어리시엔걸핑', '약세장악', '하락장악', '베어엔걸핑', '하락엔걸핑', '약세장악형', 'bearishengulfing'], file: 'bear_engulf', dir: '약세' },
  { kws: ['불리시엔걸핑', '강세장악', '상승장악', '불엔걸핑', '상승엔걸핑', 'bullishengulfing'], file: 'bull_engulf', dir: '강세' },
  { kws: ['베어하라미', '하라미', 'harami'], file: 'bear_harami', dir: '약세' },
  { kws: ['클로이스한나', '클로이스앤한나', 'cloyshanna'], file: 'cloys_hanna', dir: '중립' },
  { kws: ['역전대양봉', '인버싱슬래시', 'inversingslash'], file: 'inversing_slash', dir: '중립' },
  { kws: ['액티비티스크린', '거래활동', 'activityscreen'], file: 'activity_screen', dir: '중립' },
  // ── 하모닉(약세 변형 먼저 매칭 후 일반) ──
  { kws: ['버터플라이베어', '약세버터플라이', '베어버터플라이', 'bearishbutterfly'], file: 'butterfly_bear', dir: '약세' },
  { kws: ['사이퍼베어', '약세사이퍼', '베어사이퍼', 'bearishcypher'], file: 'cypher_bear', dir: '약세' },
  { kws: ['샤크베어', '약세샤크', '베어샤크', 'bearishshark'], file: 'shark_bear', dir: '약세' },
  { kws: ['가틀리', 'gartley'], file: 'gartley', dir: '중립' },
  { kws: ['버터플라이', '나비', 'butterfly'], file: 'butterfly', dir: '중립' },
  { kws: ['배트', '박쥐', 'bat'], file: 'bat', dir: '중립' },
  { kws: ['크랩', '게', 'crab'], file: 'crab', dir: '중립' },
  { kws: ['샤크', '상어', 'shark'], file: 'shark', dir: '중립' },
  { kws: ['사이퍼', 'cypher'], file: 'cypher', dir: '중립' },
  { kws: ['쓰리드라이브', '세번추진', 'threedrives'], file: 'three_drives', dir: '중립' },
  { kws: ['넨스타', '넨', 'nenstar'], file: 'nen_star', dir: '중립' },
  { kws: ['abcd', '에이비씨디'], file: 'abcd', dir: '중립' },
  { kws: ['소시', 'sosh'], file: 'sosh', dir: '중립' },
  { kws: ['숏터', '쇼터', 'shorter'], file: 'shorter', dir: '중립' },
  { kws: ['스나이더', 'snyder'], file: 'snyder', dir: '중립' },
  { kws: ['대안cb', 'altcb', '얼터네이트cb'], file: 'alt_cb', dir: '중립' },
  // ── 특수 ──
  { kws: ['타소닝', '연속추진', '추진', 'tasoning', 'thrust'], file: 'tasoning', dir: '강세' },
  { kws: ['스파이크', '마루보즈', '장대양봉', '대형양봉', 'marubozu', 'spike'], file: 'spike', dir: '강세' },
  { kws: ['장대음봉', '대형음봉'], file: 'spike', dir: '약세' },
  { kws: ['프랙탈', 'fractal'], file: 'fractal', dir: '중립' },
  { kws: ['abc조정', '엘리엇조정', '조정파동'], file: 'abc_correction', dir: '약세' },
  { kws: ['엘리어트', '엘리엇', '충격파', '임펄스', 'elliot', 'impulse'], file: 'elliott', dir: '강세' },
];

/** 미분류 패턴의 방향 추정(키워드 기반) */
export function inferPatternDir(n: string): PatternDir {
  const bull = ['상승', '돌파', '반등', '지지', '양봉', '매수', '골든', '바닥', '저점', '회복', '강세'];
  const bear = ['하락', '이탈', '저항', '음봉', '매도', '데드', '천장', '천정', '고점', '실패', '붕괴', '약세'];
  const isBull = bull.some((k) => n.includes(k));
  const isBear = bear.some((k) => n.includes(k));
  if (isBull && !isBear) return '강세';
  if (isBear && !isBull) return '약세';
  return '중립';
}

/** AI 패턴명 → { SVG 파일, 방향 }.
 *  1) 키워드 규칙 매칭 → 2) 미매칭 시 file '' + 방향만 추정 */
export function classifyPattern(name: string): { file: string; dir: PatternDir } {
  const n = name.replace(/\s/g, '').toLowerCase();
  for (const r of RULES) {
    if (r.kws.some((k) => n.includes(k.toLowerCase()))) return { file: r.file, dir: r.dir };
  }
  return { file: '', dir: inferPatternDir(name) };
}

/** 카테고리 + 태그로 방향 추정(차트 패턴 사전 카드용) */
export function dirFromCat(cat: string, tags: string[] = []): PatternDir {
  if (cat.includes('상승')) return '강세';
  if (cat.includes('하락')) return '약세';
  if (cat.includes('중립')) return '중립';
  if (tags.includes('상승')) return '강세';
  if (tags.includes('하락')) return '약세';
  return '중립';
}

/** 패턴 통계기반 달성률 표시 — "통계기반 달성률 78%" + 프로그레스 바.
 *  색상: 70% 이상 초록 / 60~70% 미만 노랑 / 60% 미만 회색.
 *  rate=null/undefined 면 동일한 높이 영역에 "달성률 데이터 없음" 회색 텍스트로 대체(카드 높이 일관성). */
export function SuccessRateBar({
  rate,
  className,
}: {
  rate: number | null | undefined;
  className?: string;
}) {
  // 동일 높이: 라벨(텍스트) + 4px 간격 + 바(6px) ≈ 약 28px. min-height 로 고정.
  const wrapperCls = cn('w-full', className);
  if (rate === null || rate === undefined) {
    return (
      <div className={wrapperCls} style={{ minHeight: 28 }}>
        <p className="text-[12px] text-gray-400">달성률 데이터 없음</p>
      </div>
    );
  }
  const tone =
    rate >= 70
      ? { bar: 'bg-emerald-500', text: 'text-emerald-700' }
      : rate >= 60
      ? { bar: 'bg-amber-400', text: 'text-amber-700' }
      : { bar: 'bg-gray-400', text: 'text-gray-600' };
  const pct = Math.max(0, Math.min(100, rate));
  return (
    <div className={wrapperCls} style={{ minHeight: 28 }}>
      <div className="flex items-center justify-between text-[11px] font-semibold">
        <span className="text-[var(--color-text-sub)]">통계기반 달성률</span>
        <span className={cn('tabular-nums', tone.text)}>{pct}%</span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div className={cn('h-full rounded-full', tone.bar)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** 패턴 방향 뱃지(작은 사이즈) */
export function DirBadge({ dir }: { dir: PatternDir }) {
  const cls =
    dir === '강세'
      ? 'bg-red-50 text-red-700'
      : dir === '약세'
      ? 'bg-blue-50 text-blue-700'
      : 'bg-gray-100 text-gray-600';
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold', cls)}>
      {dir}
    </span>
  );
}

/** SVG 파일명 → 사전 카드 anchor id.
 *  규칙: 선두 `숫자_카테고리_` 접두어 제거 후 소문자 슬러그.
 *  예) 01_Bearish_Head_and_Shoulders → head_and_shoulders, 26_Bullish_Cont_Bull_Flag → bull_flag.
 *  사전(ChartPatternsPage)과 링크(patternDictHref)가 동일 함수를 써서 anchor 가 일치하게 함. */
export function patternAnchor(file: string): string {
  if (!file) return '';
  return file
    .replace(
      /^\d+_(Bearish_Cont_|Bullish_Cont_|Bearish_|Bullish_|Neutral_|Gap_|Candle_|Harmonic_|Wave_|SMC_)/,
      '',
    )
    .toLowerCase();
}

/** 패턴 SVG 파일명으로 차트 패턴 사전 링크 생성(basename 포함). 미분류('')는 사전 루트로 연결 */
export function patternDictHref(file: string): string {
  const a = patternAnchor(file);
  return `${import.meta.env.BASE_URL}chart-patterns${a ? `#${a}` : ''}`;
}
